import Typography from "@/components/ui/Typography";

interface PageHeroProps {
  badge: string;
  badgeIcon?: React.ReactNode;
  title: string;
  subtitle: string;
  /** Optional icon tile rendered above the badge (e.g. support-us heart) */
  topIcon?: React.ReactNode;
  children?: React.ReactNode;
}

export function StatPill({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex items-center gap-2 rounded-full bg-white/10 px-5 py-2.5 backdrop-blur-sm">
      <span className="text-lg font-bold">{value}</span>
      <span className="text-xs text-blue-100/80">{label}</span>
    </div>
  );
}

export default function PageHero({
  badge,
  badgeIcon,
  title,
  subtitle,
  topIcon,
  children,
}: PageHeroProps) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-blue via-blue-hover to-blue-dark text-white">
      {/* Decorative shapes */}
      <div className="pointer-events-none absolute -left-24 -top-24 h-80 w-80 rounded-full bg-white/5" />
      <div className="pointer-events-none absolute -right-20 bottom-0 h-64 w-64 rounded-full bg-white/5" />
      <div className="pointer-events-none absolute left-1/3 top-1/2 h-44 w-44 -translate-y-1/2 rounded-full bg-white/[0.03]" />

      <div className="relative mx-auto max-w-4xl px-4 py-24 text-center sm:py-32">
        {topIcon && (
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm">
            {topIcon}
          </div>
        )}

        <span className="mb-4 inline-block rounded-full bg-white/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider backdrop-blur-sm">
          {badgeIcon && (
            <span className="inline-block mr-1.5 -mt-0.5 align-middle">
              {badgeIcon}
            </span>
          )}
          {badge}
        </span>

        <Typography
          variant="h1"
          className="mb-5 text-white !text-3xl sm:!text-4xl md:!text-5xl lg:!text-6xl"
        >
          {title}
        </Typography>

        <Typography
          variant="subtitle1"
          className="mx-auto max-w-2xl text-blue-100/90 !text-base sm:!text-lg md:!text-xl"
        >
          {subtitle}
        </Typography>

        {children && (
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            {children}
          </div>
        )}
      </div>
    </section>
  );
}
